import type { FC, ReactNode } from 'react'

import { useRef, useState } from 'react'
import {
    ArrowRight,
    Check,
    Heart,
    RotateCcw,
    X
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { heartBurst, originOf } from '@/lib/confetti'
import { QUIZ } from '@/constants/birthday'

const LETTERS = ['A', 'B', 'C', 'D']

const LoveQuiz: FC = (): ReactNode => {
    const [step, setStep] = useState(0)
    const [picked, setPicked] = useState<number | null>(null)
    const [score, setScore] = useState(0)
    const [finished, setFinished] = useState(false)
    const cardRef = useRef<HTMLDivElement>(null)
    const questions = QUIZ.questions
    const question = questions[step]
    const answered = picked !== null
    const isLast = step === questions.length - 1

    const pick = (option: number): void => {
        if (answered) return

        setPicked(option)

        if (option === question.answer) setScore((value) => value + 1)
    }

    const next = (): void => {
        if (isLast) {
            setFinished(true)

            if (score === questions.length) void heartBurst(originOf(cardRef.current))

            return
        }

        setStep(step + 1)
        setPicked(null)
    }

    const restart = (): void => {
        setStep(0)
        setPicked(null)
        setScore(0)
        setFinished(false)
    }

    const message = score === questions.length
        ? QUIZ.messages.perfect
        : score >= 3
            ? QUIZ.messages.good
            : QUIZ.messages.low

    return (
        <div ref={cardRef} className='flex h-full flex-col rounded-[2rem] border border-primary/10 bg-white p-6 shadow-xl shadow-primary/5 sm:p-8'>
            <div className='text-center'>
                <h3 className='font-heading text-3xl font-semibold'>
                    {QUIZ.title}
                </h3>

                {!finished && (
                    <div className='mt-4 flex items-center justify-center gap-3'>
                        <p className='text-sm font-bold text-muted-foreground'>
                            {QUIZ.counter} {step + 1} / {questions.length}
                        </p>

                        <div aria-hidden className='flex gap-1.5'>
                            {questions.map((item, index) => (
                                <span
                                    key={item.question}
                                    className={cn('h-1.5 w-5 rounded-full transition-colors', index <= step ? 'bg-primary' : 'bg-primary/15')}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {finished ? (
                <div role='status' className='flex flex-1 flex-col items-center justify-center py-8 text-center'>
                    <Heart className='size-12 animate-heartbeat fill-primary text-primary motion-reduce:animate-none' />

                    <p className='mt-5 text-sm font-bold uppercase tracking-[0.2em] text-muted-foreground'>
                        {QUIZ.scoreLabel}
                    </p>

                    <p className='mt-1 font-heading text-6xl font-semibold text-primary'>
                        {score} / {questions.length}
                    </p>

                    <p className='mt-4 max-w-sm text-lg font-semibold text-pretty'>
                        {message}
                    </p>

                    <button
                        type='button'
                        onClick={restart}
                        className='mt-8 inline-flex items-center gap-2 rounded-full border border-primary/25 px-6 py-3 text-sm font-bold text-primary transition hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/30'
                    >
                        <RotateCcw className='size-4' />
                        {QUIZ.restart}
                    </button>
                </div>
            ) : (
                <div className='mt-8 flex flex-1 flex-col'>
                    <p className='text-center font-heading text-2xl font-semibold text-balance'>
                        {question.question}
                    </p>

                    <div className='mt-7 grid gap-3 sm:grid-cols-2'>
                        {question.options.map((option, index) => {
                            const correct = index === question.answer
                            const chosen = index === picked

                            return (
                                <button
                                    key={option}
                                    type='button'
                                    onClick={() => pick(index)}
                                    disabled={answered}
                                    className={cn(
                                        'flex items-center gap-3 rounded-2xl border px-4 py-3.5 text-left font-semibold transition focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/30',
                                        !answered && 'border-primary/15 bg-secondary/40 hover:-translate-y-0.5 hover:border-primary/40 hover:bg-secondary',
                                        answered && correct && 'border-primary bg-primary text-primary-foreground',
                                        answered && chosen && !correct && 'border-foreground/20 bg-muted text-muted-foreground line-through',
                                        answered && !chosen && !correct && 'border-primary/10 opacity-50'
                                    )}
                                >
                                    <span
                                        className={cn(
                                            'grid size-8 shrink-0 place-items-center rounded-full text-sm font-extrabold',
                                            answered && correct ? 'bg-white/20' : 'bg-white text-primary shadow-sm'
                                        )}
                                    >
                                        {answered && correct ? <Check className='size-4' /> : answered && chosen ? <X className='size-4' /> : LETTERS[index]}
                                    </span>

                                    {option}
                                </button>
                            )
                        })}
                    </div>

                    <div aria-live='polite' className='mt-6 min-h-7 text-center'>
                        {answered && (
                            <p className='font-bold text-primary'>
                                {picked === question.answer ? QUIZ.correct : `${QUIZ.wrong} ${question.options[question.answer]}`}
                            </p>
                        )}
                    </div>

                    <div className='mt-auto flex justify-center pt-6'>
                        <button
                            type='button'
                            onClick={next}
                            disabled={!answered}
                            className='inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-bold text-primary-foreground shadow-lg shadow-primary/25 transition hover:brightness-110 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/30 disabled:cursor-default disabled:opacity-40 disabled:shadow-none'
                        >
                            {isLast ? QUIZ.result : QUIZ.next}
                            <ArrowRight className='size-4' />
                        </button>
                    </div>
                </div>
            )}
        </div>
    )
}

export default LoveQuiz
